<template>
	<div v-if="!loading">
		<h3>Company Pricing Summary</h3>
	</div>

	<div class="card">
		<!-- Services header with edit lock/unlock -->
		<div class="grid">
			<!-- Title -->
			<div class="col">
				<div class="grid">
					<div class="col-6">
						<Multiselect :options="activeCustomersWithProperties"
									 :searchable="true"
									 :limit="40"
									 :filter="true"
									 class="flex"
									 v-model="selectedCompanyId"
									 valueProp="id"
									 label="nameAndId"
									 track-by="nameAndId"
									 placeholder="Select a customer"
									 @select="changeCompany"
									 />
					</div>
				</div>
			</div>

			<!-- Lock/Unlock-->
			<div class="col-fixed align-self-end align-items-center">
				<ProgressSpinner style="width:40px;height:40px" strokeWidth="3" v-if="loading" />
				<a @click="editMode=!editMode">
					<i v-if="editMode" class="pi pi-lock-open" style="font-size: 1.5rem" />
					<i v-if="!editMode" class="pi pi-lock" style="font-size: 1.5rem" />
				</a>
			</div>
		</div>

		<!-- Filter text box -->
		<div style="margin:10px;" class="flex">
			<div class="w-100pc p-input-icon-left p-input-icon-right">
				<i class="pi pi-home" />
				<InputText v-model="companyServicesFilter" placeholder="Service keyword filter.." />
				<i class="pi pi-search" />
			</div>
		</div>

		<!--Pricing Summary table-->
		<DataView :value="filteredServices"
				  datakey="propertyServiceId"
				  :paginator="true"
				  :rows="10"
				  :scrollable="true"
				  stripedRows
				  class="p-datatable-sm property-service-table"
				  :paginatorTemplate="paginatorTemplate"
				  :rowsPerPageOptions="[10,20,50,100,200]"
				  responsiveLayout="scroll"
				  :alwaysShowPaginator="false"
				  currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">

			<template #header>
				<div class="col-12">
					<div class="grid flex-nowrap">
						<div class="col-3 md:col-2">Property Name</div>
						<div class="col-3">Service</div>
						<div class="col-fixed hidden lg:block service-ref">Ref</div>
						<div class="col-fixed hidden xl:block service-type">Type</div>
						<div class="text-center col-1">Anniversary</div>
						<div class="col-3 hidden md:block">Supplier</div>
						<div class="col-fixed text-right service-price">Price / Interval</div>
					</div>
				</div>
			</template>

			<template #list="{data}">
				<div class="col-12 property-service-item hover-row">

					<!-- First data section has service name / ref / supplier / price  -->
					<div class="grid flex-nowrap">
						<div class="col-3 md:col-2 ml-2 compass-red">
							<router-link :to="{ name: 'PropertyDetails', params: { propertyId: data.propertyId }}" v-tooltip.top="data.buildingName">{{data.propertyName}}</router-link>
						</div>
						<div class="col-3 service-name compass-red">
							<router-link :to="{ name: 'PropertyDetails', params: { propertyId: data.propertyId }}">{{data.serviceName}}</router-link>
						</div>
						<div class="col-fixed hidden lg:block service-ref">
							<span>{{data.ref}}</span>
						</div>
						<div class="col-fixed hidden xl:block service-type">
							<span v-if="data.serviceType">{{data.serviceType.replace(' Service','')}}</span>
						</div>
						<div class="text-center col-1">
							<span>{{formattedDate(data.whenNextServiceAnniversary)}}</span>
						</div>
						<div class="col-3 hidden md:block">
							<Multiselect :options="supplierList"
										 :searchable="true"
										 :limit="40"
										 :filter="true"
										 class="flex"
										 v-model="data.supplierCompanyId"
										 valueProp="id"
										 label="nameAndId"
										 track-by="nameAndId"
										 placeholder="Select a Supplier"
										 @select="updateSupplierName(data)"
										 @clear="clearSupplierName(data)"
										 v-if="editMode" />
							<div class="flex" v-if="!editMode">
								<span v-if="data.supplierName">{{data.supplierName}}</span>
								&nbsp;
								<a v-if="data.supplierCompanyId" @click="toggleSupplierDetails(data.supplierCompanyId)">
									<i class="pi pi-info-circle" />
								</a>
							</div>
						</div>
						<div class="col-fixed text-right service-price">
							<div class="grid flex-nowrap p-2" v-if="editMode">
								<InputNumber mode="decimal" v-model="data.price" />
								<span class="mt-2 ml-2" v-tooltip.top="'invoicing interval'">{{data.intervalMonths}}</span>
							</div>
							<div v-if="!editMode">
								<span>${{data.price}} /</span><span v-tooltip.top="'invoicing interval'"> {{data.intervalMonths}}</span>
							</div>
						</div>
					</div>
				</div>
			</template>

			<template #empty>
				<div class="text-center pt-2 pb-2">
					No records found.
				</div>
			</template>
		</DataView>
	</div>

	<!-- Floating Save/Cancel buttons -->
	<Button class="sticky-button cancel" icon="pi pi-times" :label="hasChanges ? 'Cancel' : 'Close'" @click="cancel" v-if="!loading" />
	<Button class="sticky-button" icon="pi pi-check" label="Save" @click="save" v-if="!loading" :disabled="!hasChanges" />

</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import { PricingSummary, PropertyServiceData } from '../api-client/api-client';
	import { CompanyListFlags } from '@/utils/CompanyListFlags';
	import filterListBySearchString from '@/utils/StringSearch';
	import { format, isValid } from 'date-fns';
	import { allowLeaveOnlyWithConfirmationOrNoChanges } from '@/components/LeaveWithoutSavingPrompt';
	import Toast from '@/utils/Toast';
	import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
	import { DataTableRowClickEvent } from 'primevue/datatable';

	Vue.registerHooks([
		'beforeRouteLeave',
	]);

	@Options({
		props: {
			companyId: 0
		}
	})
	export default class CompanyDetails extends Vue {

		companyId!: number;  // Actually passed as a prop

		selectedCompanyId!: number;

		receivedDataJSON = "";

		pricingSummary: PricingSummary = new PricingSummary();

		companyServicesFilter = "";

		loading = true;

		editMode = false;

		created() {
			// Ensure the company list is loaded
			if (this.$store.state.companyList.data == null || this.$store.state.companyList.data.length == 0) {
				this.$store.dispatch('downloadCompanyList');
			}

			this.selectedCompanyId = this.companyId;

			if (this.companyId < 0) {
				this.loading = false;

				// So we can detect if there are changes:
				this.receivedDataJSON = JSON.stringify(this.pricingSummary, null, 1);
				return;
			}
			this.getPricingSummary();
		}

		getPricingSummary() {
			// Retrieve data from API
			const apiClient = CompassApi.companyApi(this.$store);
			apiClient.getCompanyPricingSummaryById(this.companyId)
				.then(result => {
					if (result) {
						this.loading = false;
						this.pricingSummary = result;
					}

					// So we can detect if there are changes:
					this.receivedDataJSON = JSON.stringify(result, null, 1);
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError('Error loading pricing summary for company ' + this.companyId, error);
				});
		}

		changeCompany() {
			const newUrl = window.location.href.replace("/" + this.companyId, "/" + JSON.stringify(this.selectedCompanyId));
			window.location.replace(newUrl);
		}

		// Confirm dialog
		  beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
			allowLeaveOnlyWithConfirmationOrNoChanges(this, this.hasChanges, next);
		}

		propertyClick(ev: DataTableRowClickEvent) {
			this.$router.push({
				name: 'PropertyDetails',
				params: { propertyId: ev.data.propertyId }
			});
		}

		updateSupplierName(data: PropertyServiceData) {
			data.supplierName = this.supplierList.find(s => s.id == data.supplierCompanyId)?.name;
		}

		// Actions
		save() {
			// Lock the services
			this.editMode = false;

			// Don't need to save (and change whenUpdated) if there are no changes
			if (!this.hasChanges) {
				this.$router.go(-1);
				return;
			}

			// save services
			const apiClient = CompassApi.companyApi(this.$store);
			apiClient.updateCompanyPricingSummary(this.pricingSummary.services as PropertyServiceData[])
				.then(result => {
					// Remember the data as saved, so we only enable save button etc if there are changes after this
					this.receivedDataJSON = JSON.stringify(result, null, 1);

					Toast.RemoveAllGroups();
					Toast.Success('Save', 'Services have been saved.');
				})
				.catch(error => {
					Toast.ApiError('Error saving services for company ' + this.companyId, error);
				});
		}

		cancel() {
			this.$router.go(-1);
		}

		// helpers
		formattedDate(date: Date) {
			if (!isValid(date)) return;

			return format(date, 'dd MMM yyyy');
		}

		// Computed
		get filteredServices() {
			let list = this.pricingSummary.services as PropertyServiceData[];

			list = filterListBySearchString(list, this.companyServicesFilter,
				(el) => el.propertyId
					+ ' ' + el.propertyName
					+ ' ' + el.buildingName
					+ ' ' + el.serviceName
					+ ' ' + el.serviceType
					+ ' ' + el.ref
					+ ' ' + el.supplierName
					+ ' ' + el.whenNextServiceAnniversary);

			return list;
		}

		get supplierList() {
			return this.$store.state.companyList.data.filter(c => CompanyListFlags.isSupplier(c));
		}

		get activeCustomersWithProperties() {
			return this.$store.state.companyList.data.filter(c => c.id == this.companyId || (CompanyListFlags.isCustomer(c) && c.np != null && c.np > 0 && CompanyListFlags.isActive(c)));
		}

		get hasChanges(): boolean {
			const currentJSON = JSON.stringify(this.pricingSummary, null, 1);
			return (currentJSON != this.receivedDataJSON);
		}

		// Computed isDesktop
		get isDesktop() {
			return window.innerWidth >= 992;
		}

		// Computed to simplify paginator on mobile
		get paginatorTemplate() {
			return this.isDesktop ?
				"CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
				: "PrevPageLink NextPageLink"
		}
	}
</script>

<style scoped lang="scss">
</style>